import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2, Star, Upload, FolderOpen, ExternalLink, GripVertical, Monitor, Tablet, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";
import { ConfirmModal } from "@/pages/admin/AdminPortfolio";

const CATEGORIES = ["Graphic Design", "Branding", "Motion Design", "Video Editing", "UI/UX", "Web Development"];
const YEARS = Array.from({ length: 9 }, (_, i) => String(2026 - i));

const DEVICES = [
  { id: "desktop", label: "Desktop", icon: Monitor, width: 1280 },
  { id: "tablet", label: "Tablet", icon: Tablet, width: 834 },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: 390 },
];
const GAP_PX = { tight: 10, normal: 18, loose: 30 };
const WIDTH_PCT = { normal: "84%", wide: "94%", full: "100%" };

function Pill({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
        active
          ? "border-white bg-white text-black"
          : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

const EMPTY = {
  title: "", subtitle: "", slug: "", body: "", category: "Graphic Design", categories: ["Graphic Design"],
  year: "2026", client: "", role: "", tools: [], cover_media: null, show_cover: true, is_selected: true, gallery: [],
  gallery_layout: { columns_desktop: 0, columns_tablet: 0, columns_mobile: 0, gap: "normal", width: "normal" },
  status: "draft",
};

export default function PortfolioForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [toolInput, setToolInput] = useState("");
  const [saving, setSaving] = useState("");
  const [pickerFor, setPickerFor] = useState(null); // "cover" | "gallery" | null
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const dragIndex = useRef(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const previewOuter = useRef(null);
  const previewInner = useRef(null);
  const [previewBox, setPreviewBox] = useState({ scale: 1, height: 320 });

  const gl = form.gallery_layout || EMPTY.gallery_layout;
  const setLayout = (k, v) => set("gallery_layout", { ...gl, [k]: v });
  const device = DEVICES.find((d) => d.id === previewDevice);
  const previewCols =
    previewDevice === "mobile"
      ? gl.columns_mobile || 1
      : previewDevice === "tablet"
      ? gl.columns_tablet || (gl.columns_desktop ? Math.min(gl.columns_desktop, 2) : 1)
      : gl.columns_desktop || gl.columns_tablet || gl.columns_mobile || 2;
  const isAuto =
    (previewDevice === "desktop" && !gl.columns_desktop) ||
    (previewDevice === "tablet" && !gl.columns_tablet) ||
    (previewDevice === "mobile" && !gl.columns_mobile);

  useEffect(() => {
    const measure = () => {
      const outer = previewOuter.current;
      const inner = previewInner.current;
      if (!outer || !inner) return;
      const scale = outer.clientWidth / device.width;
      setPreviewBox({ scale, height: Math.max(inner.offsetHeight * scale, 120) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [device.width, form.gallery_layout, form.gallery.length, previewDevice]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/portfolio/${id}`).then((r) => {
      const d = r.data;
      if (!d.categories?.length && d.category) d.categories = [d.category];
      if (!d.gallery_layout) d.gallery_layout = { ...EMPTY.gallery_layout };
      setForm({ ...EMPTY, ...d });
      setSlugTouched(true);
    }).catch(() => {
      toast.error("Project not found");
      navigate("/admin/portfolio");
    });
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    set("title", v);
    if (!slugTouched) set("slug", v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const addTool = () => {
    const t = toolInput.trim();
    if (t && !form.tools.includes(t)) set("tools", [...form.tools, t]);
    setToolInput("");
  };

  const uploadFiles = async (files, target) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const items = data.map((m) => ({ type: m.type, url: m.url, alt: m.filename, orientation: "landscape" }));
      if (target === "cover" && items[0]) set("cover_media", items[0]);
      if (target === "gallery") set("gallery", [...form.gallery, ...items]);
      toast.success(`${items.length} file(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onPick = (m) => {
    const item = { type: m.type, url: m.url, alt: m.filename, orientation: "landscape" };
    if (pickerFor === "cover") set("cover_media", item);
    if (pickerFor === "gallery") set("gallery", [...form.gallery, item]);
    setPickerFor(null);
  };

  const moveItem = (from, to) => {
    const g = [...form.gallery];
    const [it] = g.splice(from, 1);
    g.splice(to, 0, it);
    set("gallery", g);
  };

  const save = async (status) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(status);
    const payload = { ...form, status };
    try {
      if (isEdit) {
        const { data } = await api.put(`/admin/portfolio/${id}`, payload);
        setForm({ ...EMPTY, ...data });
      } else {
        const { data } = await api.post("/admin/portfolio", payload);
        toast.success(status === "published" ? "Project published" : "Draft saved");
        navigate(`/admin/portfolio/${data.id}/edit`, { replace: true });
        setSaving("");
        return;
      }
      toast.success(status === "published" ? "Project published" : "Draft saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving("");
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/portfolio/${id}`);
      toast.success("Project deleted");
      navigate("/admin/portfolio");
    } catch {
      toast.error("Could not delete project");
    }
  };

  return (
    <div className="mx-auto max-w-4xl pb-28" data-testid="portfolio-form-page">
      <div className="mb-8 flex items-center gap-4">
        <Link to="/admin/portfolio" aria-label="Back" data-testid="form-back-link" className="glass flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {isEdit ? "Edit Project" : "New Project"}
        </h1>
      </div>

      {/* Basic info */}
      <section className="glass rounded-3xl p-6 md:p-8">
        <p className="eyebrow mb-6">Basic Information</p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="label-admin" htmlFor="pf-title">Title</label>
            <input id="pf-title" value={form.title} onChange={(e) => onTitle(e.target.value)} className="input-glass" placeholder="AURA" data-testid="form-title-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin" htmlFor="pf-subtitle">Subtitle</label>
            <input id="pf-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="input-glass" placeholder="Luxury Brand Identity & Motion" data-testid="form-subtitle-input" />
          </div>
          <div>
            <label className="label-admin" htmlFor="pf-slug">Slug</label>
            <input id="pf-slug" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", e.target.value); }} className="input-glass font-mono" placeholder="aura" data-testid="form-slug-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin">Categories (select one or more)</label>
            <div className="flex flex-wrap gap-2" data-testid="form-category-select">
              {CATEGORIES.map((c) => {
                const active = (form.categories?.length ? form.categories : []).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      const cur = form.categories?.length ? [...form.categories] : [];
                      const next = active ? cur.filter((x) => x !== c) : [...cur, c];
                      set("categories", next);
                      set("category", next[0] || "");
                    }}
                    data-testid={`form-category-${c.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                      active
                        ? "border-white bg-white text-black"
                        : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="label-admin" htmlFor="pf-year">Year</label>
            <select id="pf-year" value={form.year} onChange={(e) => set("year", e.target.value)} className="input-glass" data-testid="form-year-select">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="label-admin" htmlFor="pf-client">Client</label>
            <input id="pf-client" value={form.client} onChange={(e) => set("client", e.target.value)} className="input-glass" placeholder="Client name" data-testid="form-client-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin" htmlFor="pf-role">Role</label>
            <input id="pf-role" value={form.role} onChange={(e) => set("role", e.target.value)} className="input-glass" placeholder="Brand Designer & Motion Director" data-testid="form-role-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin">Tools / Software</label>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2.5" data-testid="form-tools-input">
              {form.tools.map((t) => (
                <span key={t} className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs text-white">
                  {t}
                  <button type="button" onClick={() => set("tools", form.tools.filter((x) => x !== t))} aria-label={`Remove ${t}`} className="text-neutral-500 hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                value={toolInput}
                onChange={(e) => setToolInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTool(); } }}
                placeholder="Add tool + Enter"
                className="min-w-[140px] flex-1 bg-transparent px-2 py-1.5 text-sm text-white outline-none placeholder-neutral-500"
                data-testid="form-tools-text"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="glass mt-6 rounded-3xl p-6 md:p-8">
        <p className="eyebrow mb-6">Content</p>
        <RichTextEditor value={form.body} onChange={(v) => set("body", v)} testid="form-body-editor" />
      </section>

      {/* Cover */}
      <section className="glass mt-6 rounded-3xl p-6 md:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow">Cover Media</p>
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={form.show_cover !== false}
              onChange={(e) => set("show_cover", e.target.checked)}
              className="h-4 w-4 accent-white"
              data-testid="form-show-cover-toggle"
            />
            <span className="text-sm text-neutral-300">Show cover on project page</span>
          </label>
        </div>
        {form.cover_media?.url ? (
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            {form.cover_media.type === "video" ? (
              <video src={mediaUrl(form.cover_media.url)} controls muted className="aspect-video w-full object-cover" data-testid="cover-preview" />
            ) : (
              <img src={mediaUrl(form.cover_media.url)} alt="Cover" className="aspect-video w-full object-cover" data-testid="cover-preview" />
            )}
            <button
              type="button"
              onClick={() => set("cover_media", null)}
              aria-label="Remove cover"
              data-testid="cover-remove-button"
              className="glass-strong absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-neutral-300 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-white/15 p-10 text-center">
            <p className="text-sm text-neutral-500">No cover selected</p>
            <div className="flex gap-3">
              <label className="btn-ghost !py-2.5 text-xs cursor-pointer" data-testid="cover-upload-button">
                <Upload className="h-4 w-4" /> Upload
                <input type="file" accept="image/*,video/mp4,video/webm" className="hidden" onChange={(e) => uploadFiles(e.target.files, "cover")} />
              </label>
              <button type="button" onClick={() => setPickerFor("cover")} className="btn-ghost !py-2.5 text-xs" data-testid="cover-library-button">
                <FolderOpen className="h-4 w-4" /> From library
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Gallery */}
      <section className="glass mt-6 rounded-3xl p-6 md:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow">Gallery ({form.gallery.length})</p>
          <div className="flex gap-3">
            <label className="btn-ghost !py-2 !px-4 text-xs cursor-pointer" data-testid="gallery-upload-button">
              <Plus className="h-4 w-4" /> Upload files
              <input type="file" multiple accept="image/*,video/mp4,video/webm" className="hidden" onChange={(e) => uploadFiles(e.target.files, "gallery")} />
            </label>
            <button type="button" onClick={() => setPickerFor("gallery")} className="btn-ghost !py-2 !px-4 text-xs" data-testid="gallery-library-button">
              <FolderOpen className="h-4 w-4" /> From library
            </button>
          </div>
        </div>
        {form.gallery.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-neutral-500">
            Add images or videos — drag thumbnails to reorder.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="gallery-grid">
            {form.gallery.map((g, i) => (
              <div
                key={`${g.url}-${i}`}
                draggable
                onDragStart={() => (dragIndex.current = i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIndex.current !== null && dragIndex.current !== i) moveItem(dragIndex.current, i); dragIndex.current = null; }}
                className="group relative overflow-hidden rounded-xl border border-white/10"
                data-testid={`gallery-item-${i}`}
              >
                {g.type === "video" ? (
                  <video src={mediaUrl(g.url)} muted preload="metadata" className="aspect-square w-full object-cover" />
                ) : (
                  <img src={mediaUrl(g.url)} alt={g.alt} className="aspect-square w-full object-cover" />
                )}
                <div className="absolute inset-0 flex flex-col justify-between bg-black/50 p-2 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex justify-between">
                    <GripVertical className="h-4 w-4 cursor-grab text-neutral-400" />
                    <button type="button" onClick={() => set("gallery", form.gallery.filter((_, x) => x !== i))} aria-label="Remove" data-testid={`gallery-remove-${i}`} className="text-neutral-300 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    <select
                      value={g.orientation || "landscape"}
                      onChange={(e) => set("gallery", form.gallery.map((x, xi) => (xi === i ? { ...x, orientation: e.target.value } : x)))}
                      className="w-full rounded-lg border border-white/15 bg-black/60 px-2 py-1 text-[10px] text-white"
                      data-testid={`gallery-orientation-${i}`}
                    >
                      <option value="landscape">Landscape</option>
                      <option value="portrait">Portrait</option>
                      <option value="square">Square</option>
                      <option value="wide">Full width</option>
                      <option value="original">Original ratio</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => set("cover_media", { ...g })}
                      className="flex w-full items-center justify-center gap-1 rounded-lg border border-white/15 bg-black/60 px-2 py-1 text-[10px] text-white hover:bg-white/10"
                      data-testid={`gallery-featured-${i}`}
                    >
                      <Star className="h-3 w-3" /> Set as cover
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Gallery Layout */}
      <section className="glass mt-6 rounded-3xl p-6 md:p-8" data-testid="gallery-layout-section">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <p className="eyebrow">Gallery Layout</p>
          <div className="flex gap-2" data-testid="layout-preview-devices">
            {DEVICES.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setPreviewDevice(d.id)}
                data-testid={`layout-device-${d.id}`}
                aria-label={`Preview on ${d.label}`}
                className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                  previewDevice === d.id
                    ? "border-white bg-white text-black"
                    : "border-white/12 text-neutral-400 hover:border-white/30 hover:text-white"
                }`}
              >
                <d.icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="label-admin">Desktop columns</label>
            <div className="flex flex-wrap gap-2" data-testid="layout-cols-desktop">
              {[[0, "Auto"], [1, "1"], [2, "2"], [3, "3"]].map(([v, l]) => (
                <Pill key={v} active={(gl.columns_desktop || 0) === v} onClick={() => setLayout("columns_desktop", v)} testid={`layout-desktop-${v}`}>{l}</Pill>
              ))}
            </div>
          </div>
          <div>
            <label className="label-admin">Tablet columns</label>
            <div className="flex flex-wrap gap-2" data-testid="layout-cols-tablet">
              {[[0, "Auto"], [1, "1"], [2, "2"]].map(([v, l]) => (
                <Pill key={v} active={(gl.columns_tablet || 0) === v} onClick={() => setLayout("columns_tablet", v)} testid={`layout-tablet-${v}`}>{l}</Pill>
              ))}
            </div>
          </div>
          <div>
            <label className="label-admin">Mobile columns</label>
            <div className="flex flex-wrap gap-2" data-testid="layout-cols-mobile">
              {[[0, "Auto"], [1, "1"], [2, "2"]].map(([v, l]) => (
                <Pill key={v} active={(gl.columns_mobile || 0) === v} onClick={() => setLayout("columns_mobile", v)} testid={`layout-mobile-${v}`}>{l}</Pill>
              ))}
            </div>
          </div>
          <div>
            <label className="label-admin">Spacing</label>
            <div className="flex flex-wrap gap-2" data-testid="layout-gap">
              {[["tight", "Tight"], ["normal", "Normal"], ["loose", "Loose"]].map(([v, l]) => (
                <Pill key={v} active={(gl.gap || "normal") === v} onClick={() => setLayout("gap", v)} testid={`layout-gap-${v}`}>{l}</Pill>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="label-admin">Gallery width</label>
            <div className="flex flex-wrap gap-2" data-testid="layout-width">
              {[["normal", "Normal"], ["wide", "Wide"], ["full", "Full bleed"]].map(([v, l]) => (
                <Pill key={v} active={(gl.width || "normal") === v} onClick={() => setLayout("width", v)} testid={`layout-width-${v}`}>{l}</Pill>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <label className="label-admin">
            Preview — {device.label} {isAuto ? "(auto editorial mix)" : `${previewCols} column${previewCols > 1 ? "s" : ""}`}
          </label>
          <div
            ref={previewOuter}
            className="overflow-hidden rounded-2xl border border-white/10 bg-black/40"
            style={{ height: previewBox.height }}
            data-testid="layout-preview"
          >
            <div
              ref={previewInner}
              style={{ width: device.width, transform: `scale(${previewBox.scale})`, transformOrigin: "top left" }}
              className="bg-[#0a0a0a] px-[6%] py-[5%]"
            >
              <div className="mb-2 h-2.5 w-16 rounded-full bg-white/15" />
              <div className="mb-8 h-5 w-2/5 rounded-full bg-white/25" />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${previewCols}, 1fr)`,
                  gap: GAP_PX[gl.gap] || GAP_PX.normal,
                  width: WIDTH_PCT[gl.width] || WIDTH_PCT.normal,
                  margin: "0 auto",
                }}
              >
                {(form.gallery.length ? form.gallery.slice(0, 6) : Array.from({ length: previewCols * 2 })).map((g, i) =>
                  g?.url && g.type !== "video" ? (
                    <img
                      key={g.url + i}
                      src={mediaUrl(g.url)}
                      alt=""
                      style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 10 }}
                    />
                  ) : (
                    <div
                      key={i}
                      style={{ width: "100%", aspectRatio: "4 / 3", borderRadius: 10 }}
                      className="flex items-center justify-center border border-white/10 bg-white/[0.05] font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                    >
                      {g?.type === "video" ? "Video" : "Image"}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="glass-strong sticky bottom-4 z-30 mt-8 flex flex-wrap items-center gap-3 rounded-full px-5 py-3" data-testid="form-actions">
        <label className="flex cursor-pointer items-center gap-2.5 px-2">
          <input
            type="checkbox"
            checked={form.is_selected !== false}
            onChange={(e) => set("is_selected", e.target.checked)}
            className="h-4 w-4 accent-white"
            data-testid="form-selected-work-toggle"
          />
          <span className="text-xs text-neutral-300">Selected Work on homepage</span>
        </label>
        <button type="button" onClick={() => save("draft")} disabled={Boolean(saving)} className="btn-ghost !py-2.5 text-xs disabled:opacity-50" data-testid="save-draft-button">
          {saving === "draft" ? "Saving…" : "Save Draft"}
        </button>
        <button type="button" onClick={() => save("published")} disabled={Boolean(saving)} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="publish-button">
          {saving === "published" ? "Publishing…" : form.status === "published" ? "Update" : "Publish"}
        </button>
        {isEdit && form.slug && (
          <a href={`/work/${form.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5 text-xs" data-testid="preview-button">
            <ExternalLink className="h-4 w-4" /> Preview
          </a>
        )}
        {isEdit && (
          <button type="button" onClick={() => setConfirmDelete(true)} className="ml-auto inline-flex items-center gap-2 rounded-full border border-red-500/30 px-5 py-2.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/10" data-testid="delete-project-button">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
      </div>

      <MediaPicker open={Boolean(pickerFor)} onClose={() => setPickerFor(null)} onSelect={onPick} />
      {confirmDelete && (
        <ConfirmModal target={form} noun="project" onCancel={() => setConfirmDelete(false)} onConfirm={doDelete} />
      )}
    </div>
  );
}
